// Copyright (C) 2026, Meritic Inc.

/**
 * Newton URL properties — combines REACT_APP_NEWTON_ADDRESS (base) with each
 * relative API path env var at call time. This avoids ${VAR} interpolation in
 * env files, which Docker env_file does not support.
 *
 * @author Nipun Gupta (nipun@getanko.com)
 */

const base = (): string => process.env.REACT_APP_NEWTON_ADDRESS ?? "";

// Data Model
export const getDataModelUrl = (): string => `${base()}${process.env.REACT_APP_DATAMODEL_API ?? ""}`;
export const getDataModelInfoUrl = (): string => `${base()}${process.env.REACT_APP_DATAMODEL_INFO_API ?? ""}`;
export const getDataModelCreateUrl = (): string => `${base()}${process.env.REACT_APP_DATAMODEL_CREATE_API ?? ""}`;
export const getDataModelUpdateUrl = (): string => `${base()}${process.env.REACT_APP_DATAMODEL_UPDATE_API ?? ""}`;
export const getListDataModelUrl = (): string => `${base()}${process.env.REACT_APP_LIST_DATAMODEL_API ?? ""}`;
export const getSyncTsWorksheetUrl = (): string => `${base()}${process.env.REACT_APP_SYNC_TS_WORKSHEET_API ?? ""}`;

// KG Column
export const getKgColumnUrl = (): string => `${base()}${process.env.REACT_APP_KG_COLUMN_API ?? ""}`;
export const getAllKgColumnsUrl = (): string => `${base()}${process.env.REACT_APP_GET_ALL_KG_COLUMNS_API ?? ""}`;
export const putKgColumnUrl = (): string => `${base()}${process.env.REACT_APP_PUT_KG_COLUMN_API ?? ""}`;
export const updateKgColumnUrl = (): string => `${base()}${process.env.REACT_APP_UPDATE_KG_COLUMN_API ?? ""}`;
export const getKgColumnValuesUrl = (): string => `${base()}${process.env.REACT_APP_GET_KG_COLUMN_VALUES_API ?? ""}`;
export const getMatchingKgColumnsUrl = (): string =>
  `${base()}${process.env.REACT_APP_GET_MATCHING_KG_COLUMNS_API ?? ""}`;

// Metadata
export const getMetadataDependencyUrl = (): string =>
  `${base()}${process.env.REACT_APP_GET_METADATA_DEPENDENCY_API ?? ""}`;
export const updateMetadataHeaderUrl = (): string =>
  `${base()}${process.env.REACT_APP_UPDATE_METADATA_HEADER_API ?? ""}`;
export const getMetadataHeadersUrl = (): string => `${base()}${process.env.REACT_APP_GET_METADATA_HEADERS_API ?? ""}`;

// Metric
export const getMetricUrl = (): string => `${base()}${process.env.REACT_APP_METRIC_API ?? ""}`;
export const listMetricsUrl = (): string => `${base()}${process.env.REACT_APP_LIST_METRIC_API ?? ""}`;
export const createMetricUrl = (): string => `${base()}${process.env.REACT_APP_CREATE_METRIC_API ?? ""}`;
export const updateMetricUrl = (): string => `${base()}${process.env.REACT_APP_UPDATE_METRIC_API ?? ""}`;
export const getAllMetricsUrl = (): string => `${base()}${process.env.REACT_APP_GET_ALL_METRICS_API ?? ""}`;

// Benchmark
export const getBenchmarkUrl = (): string => `${base()}${process.env.REACT_APP_BENCHMARK_API ?? ""}`;
export const putBenchmarkUrl = (): string => `${base()}${process.env.REACT_APP_PUT_BENCHMARK_API ?? ""}`;
export const getAllBenchmarksUrl = (): string => `${base()}${process.env.REACT_APP_GET_ALL_BENCHMARKS_API ?? ""}`;

// Session
export const getSessionInfoUrl = (): string => `${base()}${process.env.REACT_APP_SESSION_INFO_API ?? ""}`;
export const getLogoutUrl = (): string => `${base()}${process.env.REACT_APP_LOGOUT_API ?? ""}`;
export const getLoginUrl = (): string => `${base()}${process.env.REACT_APP_LOGIN_API ?? ""}`;

// User
export const getUserUrl = (): string => `${base()}${process.env.REACT_APP_USER_API ?? ""}`;
export const getCreateUserUrl = (): string => `${base()}${process.env.REACT_APP_CREATE_USER_API ?? ""}`;
export const getUpdateUserUrl = (): string => `${base()}${process.env.REACT_APP_UPDATE_USER_API ?? ""}`;
export const getListUsersUrl = (): string => `${base()}${process.env.REACT_APP_LIST_USERS_API ?? ""}`;

// Password Reset
export const getPasswordForgotUrl = (): string => `${base()}${process.env.REACT_APP_PASSWORD_FORGOT_API ?? ""}`;
export const getPasswordVerifyOtpUrl = (): string => `${base()}${process.env.REACT_APP_PASSWORD_VERIFY_OTP_API ?? ""}`;
export const getPasswordResetUrl = (): string => `${base()}${process.env.REACT_APP_PASSWORD_RESET_API ?? ""}`;

// Analysis Plan
export const getAnalysisPlanUrl = (): string => `${base()}${process.env.REACT_APP_ANALYSIS_PLAN_API ?? ""}`;
export const getUpdatePlanHeaderUrl = (): string => `${base()}${process.env.REACT_APP_UPDATE_PLAN_HEADER_API ?? ""}`;
export const getCreateOrUpdatePlanUrl = (): string =>
  `${base()}${process.env.REACT_APP_CREATE_OR_UPDATE_PLAN_API ?? ""}`;
export const getListPlansUrl = (): string => `${base()}${process.env.REACT_APP_LIST_PLANS_API ?? ""}`;

// Analyst
export const getAnalystUrl = (): string => `${base()}${process.env.REACT_APP_ANALYST_API ?? ""}`;
export const getCreateAnalystUrl = (): string => `${base()}${process.env.REACT_APP_CREATE_ANALYST_API ?? ""}`;
export const getUpdateAnalystUrl = (): string => `${base()}${process.env.REACT_APP_UPDATE_ANALYST_API ?? ""}`;
export const getPutScenariosToAnalystUrl = (): string =>
  `${base()}${process.env.REACT_APP_PUT_SCENARIOS_TO_ANALYST_API ?? ""}`;
export const getListAnalystUrl = (): string => `${base()}${process.env.REACT_APP_LIST_ANALYST_API ?? ""}`;
export const getCloneAnalystUrl = (): string => `${base()}${process.env.REACT_APP_CLONE_ANALYST_API ?? ""}`;
export const getRefreshDataScopeUrl = (): string => `${base()}${process.env.REACT_APP_REFRESH_DATA_SCOPE_API ?? ""}`;
export const getAnalystUsageUrl = (): string => `${base()}${process.env.REACT_APP_ANALYST_USAGE_API ?? ""}`;

// Data
export const getColumnValuesUrl = (): string => `${base()}${process.env.REACT_APP_GET_COLUMN_VALUES_API ?? ""}`;
export const getMkgObservationDataUrl = (): string =>
  `${base()}${process.env.REACT_APP_GET_MKG_OBSERVATION_DATA_API ?? ""}`;

// Schedule Jobs
export const getScheduleJobUrl = (): string => `${base()}${process.env.REACT_APP_SCHEDULE_JOB_API ?? ""}`;
export const getScheduleJobRunUrl = (): string => `${base()}${process.env.REACT_APP_SCHEDULE_JOB_RUN_API ?? ""}`;
export const getPutScheduledJobUrl = (): string => `${base()}${process.env.REACT_APP_PUT_SCHEDULED_JOB_API ?? ""}`;
export const getJobRunGetUrl = (): string => `${base()}${process.env.REACT_APP_GET_JOB_RUN_API ?? ""}`;
export const getToggleJobActiveUrl = (): string => `${base()}${process.env.REACT_APP_TOGGLE_JOB_ACTIVE_API ?? ""}`;
export const getListJobsUrl = (): string => `${base()}${process.env.REACT_APP_LIST_JOBS_API ?? ""}`;
export const getListJobRunsUrl = (): string => `${base()}${process.env.REACT_APP_LIST_JOB_RUNS_API ?? ""}`;
export const getRunScheduleJobUrl = (): string => `${base()}${process.env.REACT_APP_RUN_SCHEDULE_JOB_API ?? ""}`;
export const getScheduleJobUsageUrl = (): string => `${base()}${process.env.REACT_APP_SCHEDULE_JOB_USAGE_API ?? ""}`;

// Swagger
export const getNewtonSwaggerUrl = (): string => `${base()}/swagger-ui.html`;
